/**
 * Centralized SignalR Manager - Single Source of Truth
 * 
 * This replaces the complex message bus with a simpler, more reliable approach
 * that provides deduplication, logging, and centralized message handling.
 * 
 * Features:
 * - Singleton pattern for single instance across the app
 * - Message deduplication with configurable timeout
 * - Event-driven architecture using EventEmitter
 * - Automatic cleanup of old messages
 * - Performance metrics and monitoring
 * - Development logging
 */

import { EventEmitter } from 'events';
import { signalRService } from './signalRService';
import { DEDUPLICATION_STRATEGIES } from '../config/signalRConfig';

export interface SignalRManagerConfig {
  enableDeduplication: boolean;
  enableLogging: boolean;
  messageTimeout: number;
  enableSessionValidation: boolean;
  maxRetryAttempts: number;
  retryDelayMs: number;
}

export interface SessionContext {
  sessionId: string | null;
  userId: string | null;
  isActive: boolean;
}

interface DeduplicationState {
  lastContent?: string;
  lastTimestamp: number;
  suppressUntil: number;
  count: number;
}

class SignalRManager extends EventEmitter {
  private static instance: SignalRManager;
  private readonly instanceId: string;
  private initialized = false;
  private config: SignalRManagerConfig;
  private messageCache = new Map<string, { timestamp: number; data: any }>();
  private subscribers = new Map<string, Set<(...args: any[]) => void>>();
  private sessionContext: SessionContext = { sessionId: null, userId: null, isActive: false };
  private retryAttempts = new Map<string, number>();
  private deduplicationState = new Map<string, DeduplicationState>();
  
  // Performance metrics
  private performanceMetrics = {
    messagesProcessed: 0,
    duplicatesFiltered: 0,
    averageProcessingTime: 0,
    peakMemoryUsage: 0,
    lastResetTime: Date.now(),
    validationFailures: 0,
    retryAttempts: 0
  };

  private constructor(config: Partial<SignalRManagerConfig> = {}) {
    super();
    this.instanceId = Math.random().toString(36).substring(2, 8);
    this.config = {
      enableDeduplication: true,
      enableLogging: false, // Disable verbose logging, keep only errors
      messageTimeout: 30000, // 30 seconds
      enableSessionValidation: true,
      maxRetryAttempts: 3,
      retryDelayMs: 1000,
      ...config
    };
  }

  public static getInstance(config?: Partial<SignalRManagerConfig>): SignalRManager {
    if (!SignalRManager.instance) {
      SignalRManager.instance = new SignalRManager(config);
      // console.log('[SignalRManager] New singleton instance created');
    } else {
      // console.log('[SignalRManager] Returning existing singleton instance');
    }
    return SignalRManager.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      // console.log('[SignalRManager] Already initialized, skipping...');
      return;
    }

    // console.log('[SignalRManager] Initializing event listeners...');

    // Set up listeners for ONLY the normalized events (no duplicates)
    signalRService.on('status', this.handleMessage.bind(this, 'status')); 
    signalRService.on('stream', this.handleMessage.bind(this, 'stream')); 
    signalRService.on('complete', this.handleMessage.bind(this, 'complete')); 
    signalRService.on('updateSession', this.handleMessage.bind(this, 'updateSession')); 
    signalRService.on('errorUpdate', this.handleMessage.bind(this, 'errorUpdate'));
    signalRService.on('activeRunsBlocked', this.handleMessage.bind(this, 'activeRunsBlocked'));
    signalRService.on('runsStatus', this.handleMessage.bind(this, 'runsStatus')); 

    this.initialized = true;
  }

  private handleMessage(type: string, data: any): void {
    const messageId = this.generateMessageId(type, data);
    
    // Performance measurement
    const start = performance.now();
    
    // Enhanced validation
    if (!this.validateEventData(type, data)) {
      this.log(`Invalid event data for ${type}`, data);
      this.performanceMetrics.validationFailures++;
      return;
    }
    
    // Session context validation
    if (this.config.enableSessionValidation && !this.validateSessionContext(type, data)) {
      this.log(`Session context validation failed for ${type}`, { 
        data, 
        currentSession: this.sessionContext.sessionId
      });
      this.performanceMetrics.validationFailures++;
      return;
    }
    
    // Smart deduplication
    let deduplicationKey = messageId; // Default fallback
    if (this.config.enableDeduplication) {
      // Special handling for status messages - NEVER deduplicate them
      if (type === 'status') {
        // Status messages are critical for user feedback and should always pass through
      } else {
        // Standard deduplication for other types
        const smartKey = this.generateSmartDeduplicationKey(type, data);
        if (smartKey) {
          deduplicationKey = smartKey;
          const cached = this.messageCache.get(deduplicationKey);
          if (cached && (Date.now() - cached.timestamp) < this.config.messageTimeout) {
            this.performanceMetrics.duplicatesFiltered++;
            return;
          }
          this.messageCache.set(deduplicationKey, { timestamp: Date.now(), data });
        }
      }
    }

    // Try to emit with error recovery using data
    try {
      this.emitWithRetry(type, data);
      
      // Log emitted events if logging enabled
      if (this.config.enableLogging) {
        console.log(`✅ [SignalRManager] Event '${type}' emitted`, {
          type,
          messageId: this.generateMessageId(type, data),
          sessionId: data?.sessionId || data?.messageId || 'unknown'
        });
      }
    } catch (error) {
      this.handleEmitError(type, data, error);
    }

    // Update performance metrics
    const duration = performance.now() - start;
    this.performanceMetrics.messagesProcessed++;
    this.performanceMetrics.averageProcessingTime = 
      (this.performanceMetrics.averageProcessingTime + duration) / 2;
    
    if (duration > 10) { // Log slow operations
      console.warn(`[SignalRManager] Slow message processing: ${type} took ${duration.toFixed(2)}ms`);
    }

    // this.log(`Message processed: ${type}`, deduplicationKey || 'no-dedup');
    this.cleanupOldMessages();
  }

  private generateMessageId(type: string, data: any): string {
    if (type === 'stream' && data?.messageId && data?.chunk) {
      // Use a more specific ID for stream chunks to avoid over-deduplication
      const chunkPreview = data.chunk.slice(0, 20).replace(/\s+/g, '_');
      return `${type}:${data.messageId}:${chunkPreview}:${data.isComplete}`;
    }
    if (type === 'complete' && data?.messageId) {
      return `${type}:${data.messageId}`;
    }
    if (type === 'status' && data?.messageId) {
      // CRITICAL: Status messages should NEVER be deduplicated - always unique
      // Each status update is important for user feedback
      return `${type}:${data.messageId}:${data.status}:${Date.now()}:${Math.random()}`;
    }
    if (type === 'updateSession' && data?.sessionId) {
      // Include processing state and title status to avoid over-deduplication
      const processingState = data.isProcessing ? 'processing' : 'complete';
      const hasTitle = data.title ? 'withTitle' : 'noTitle';
      return `${type}:${data.sessionId}:${processingState}:${hasTitle}`;
    }
    return `${type}:${data?.messageId || data?.sessionId || Date.now()}`;
  }

  private getDeduplicationStrategy(type: string): 'NEVER' | 'CONTENT' | 'IDENTITY' {
    for (const [strategy, types] of Object.entries(DEDUPLICATION_STRATEGIES)) {
      if (types.includes(type)) {
        return strategy as 'NEVER' | 'CONTENT' | 'IDENTITY';
      }
    }
    return 'IDENTITY'; // Default strategy
  }

  private generateSmartDeduplicationKey(type: string, data: any): string | null {
    const strategy = this.getDeduplicationStrategy(type);
    
    switch (strategy) {
      case 'NEVER':
        return null; // No deduplication
        
      case 'CONTENT':
        if (type === 'stream' && data?.messageId && data?.chunk) {
          const contentHash = this.hashContent(data.chunk);
          return `${type}:${data.messageId}:${contentHash}:${data.isComplete}`;
        }
        break;
        
      case 'IDENTITY':
        if (type === 'complete' && data?.messageId) {
          return `${type}:${data.messageId}`;
        }
        if (type === 'updateSession' && data?.sessionId) {
          return `${type}:${data.sessionId}:${data.isProcessing || false}`;
        }
        if (data?.messageId) {
          return `${type}:${data.messageId}`;
        }
        break;
    }
    
    return `${type}:${Date.now()}`; // Fallback
  }

  private hashContent(content: string): string {
    return content.length + ':' + content.slice(0, 10).replace(/\s+/g, '_');
  }

  private validateEventData(type: string, data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    switch (type) {
      case 'stream':
        return !!(data.messageId && typeof data.chunk === 'string');
      case 'complete':
        return !!(data.messageId);
      case 'status':
        return !!(data.messageId);
      case 'updateSession':
        return !!(data.sessionId);
      case 'error':
        return !!(data.message || data.error);
      case 'activeRunsBlocked':
        return !!(data.sessionId);
      case 'runsStatus':
        return !!(data.sessionId || data.type);
      default:
        return true; // Allow unknown event types
    }
  }

  private validateSessionContext(type: string, data: any): boolean {
    // Skip validation if no active session
    if (!this.sessionContext.isActive) {
      return true;
    }

    // Extract session ID from different event types
    let eventSessionId: string | null = null;
    
    if (type === 'updateSession' && data.sessionId) {
      eventSessionId = data.sessionId;
    } else if ((type === 'stream' || type === 'complete' || type === 'status') && data.sessionId) {
      eventSessionId = data.sessionId;
    }

    // If we can't extract session ID, allow the event (might be global)
    if (!eventSessionId) {
      return true;
    }

    // Validate against current session
    return eventSessionId === this.sessionContext.sessionId;
  }

  private emitWithRetry(type: string, data: any): void {
    const retryKey = `${type}:${Date.now()}`;
    let attempts = 0;

    const attemptEmit = () => {
      try {
        this.emit(type, data);
        this.emit('*', { type, data }); // Global listener
        
        // Reset retry count on success
        this.retryAttempts.delete(retryKey);
      } catch (error) {
        attempts++;
        this.performanceMetrics.retryAttempts++;
        
        if (attempts < this.config.maxRetryAttempts) {
          this.log(`Emit retry ${attempts}/${this.config.maxRetryAttempts} for ${type}`, error);
          setTimeout(attemptEmit, this.config.retryDelayMs * attempts);
        } else {
          this.log(`Emit failed after ${attempts} attempts for ${type}`, error);
          throw error;
        }
      }
    };

    attemptEmit();
  }

  private handleEmitError(type: string, data: any, error: any): void {
    this.log(`Critical emit error for ${type}`, { data, error });
    
    // Try to emit error event if possible
    try {
      this.emit('error', {
        type: 'emit_failure',
        originalEvent: type,
        originalData: data,
        error: error.message || error
      });
    } catch (secondaryError) {
      console.error('[SignalRManager] Critical: Failed to emit error event', secondaryError);
    }
  }

  private cleanupOldMessages(): void {
    const cutoff = Date.now() - this.config.messageTimeout;
    const entriesToDelete: string[] = [];
    
    // Cleanup message cache
    this.messageCache.forEach((cached, id) => {
      if (cached.timestamp < cutoff) {
        entriesToDelete.push(id);
      }
    });
    
    entriesToDelete.forEach(id => this.messageCache.delete(id));
    
    // Cleanup deduplication state
    const stateEntriesToDelete: string[] = [];
    this.deduplicationState.forEach((state, key) => {
      if (state.lastTimestamp < cutoff) {
        stateEntriesToDelete.push(key);
      }
    });
    
    stateEntriesToDelete.forEach(key => this.deduplicationState.delete(key));
  }

  private log(message: string, data?: any): void {
    // Only log errors, not verbose debug information
    if (this.config.enableLogging && (message.includes('error') || message.includes('Error') || message.includes('failed') || message.includes('Failed'))) {
      console.error(`[SignalRManager:${this.instanceId}] ${message}`, data || '');
    }
  }

  // Public API
  subscribe(eventType: string, handler: (...args: any[]) => void): () => void {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }
    this.subscribers.get(eventType)!.add(handler);
    this.on(eventType, handler);

    return () => {
      this.subscribers.get(eventType)?.delete(handler);
      this.off(eventType, handler);
    };
  }

  // Session management
  setSessionContext(sessionId: string | null, userId: string | null): void {
    const wasActive = this.sessionContext.isActive;
    this.sessionContext = {
      sessionId,
      userId,
      isActive: !!(sessionId && userId)
    };
    
    this.log(`Session context updated`, {
      sessionId,
      userId,
      wasActive,
      isActive: this.sessionContext.isActive
    });
  }

  getSessionContext(): SessionContext {
    return { ...this.sessionContext };
  }

  clearSessionContext(): void {
    this.sessionContext = { sessionId: null, userId: null, isActive: false };
    this.log('Session context cleared');
  }

  getStats() {
    return {
      initialized: this.initialized,
      cacheSize: this.messageCache.size,
      deduplicationStateSize: this.deduplicationState.size,
      subscriberCount: Array.from(this.subscribers.values()).reduce((total, set) => total + set.size, 0),
      eventTypes: Array.from(this.subscribers.keys())
    };
  }

  getPerformanceMetrics() {
    return {
      ...this.performanceMetrics,
      cacheSize: this.messageCache.size,
      uptime: Date.now() - this.performanceMetrics.lastResetTime,
      memoryUsage: (performance as any).memory?.usedJSHeapSize || 0
    };
  }

  resetMetrics() {
    this.performanceMetrics = {
      messagesProcessed: 0,
      duplicatesFiltered: 0,
      averageProcessingTime: 0,
      peakMemoryUsage: 0,
      lastResetTime: Date.now(),
      validationFailures: 0,
      retryAttempts: 0
    };
    this.log('Performance metrics reset');
  }

  // For testing and debugging
  clearCache(): void {
    this.messageCache.clear();
    this.deduplicationState.clear();
    this.log('Message cache and deduplication state cleared');
  }

  // Override emit to prevent debug logging
  emit(eventName: string | symbol, ...args: any[]): boolean {
    // Call parent emit without any logging
    return super.emit(eventName, ...args);
  }

  destroy(): void {
    this.removeAllListeners();
    this.messageCache.clear();
    this.deduplicationState.clear();
    this.subscribers.clear();
    this.initialized = false;
    this.log('SignalR Manager destroyed');
  }
}

export const signalRManager = SignalRManager.getInstance({
  enableDeduplication: true,
  enableLogging: false, // Disable all verbose logging
  messageTimeout: 30000,
  enableSessionValidation: true,
  maxRetryAttempts: 3,
  retryDelayMs: 1000
});
